import React, { useEffect, useState } from "react";
import { Box, IconButton } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import AddIcon from "@material-ui/icons/Add";
import { useFilterOperationOptions } from "pages/CreateSchedulerRule/hooks/useFilterOperationOptions";
import { useSelectOptions } from "hooks";
import { useSelector } from "react-redux";
import { AsyncAutocomplete, HiddenTextField, Select } from "components";
import { getFiltersFields } from "store/features/filters";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { FilterViewModel } from "api/factories/ulits.factory";
import { ArrayField } from "react-hook-form/dist/types";
import { useFilterAutocomplete } from "hooks/api";

interface FieldFilterProps {
    index: number;
    item: Partial<ArrayField<FilterViewModel>>;
    source: string;
    isOrphan: boolean;
    hasLogicalOperator?: boolean;
    disableBackground: boolean;
    onInsertFilter(index: number): void;
    onRemoveFilter(index?: number | number[] | undefined): void;
}

export const logicalOperators = [
    { name: "Or", value: "Or" },
    { name: "And", value: "And" },
];

/**
 * @param index Row number
 * @param source autocomplete url
 * @param item Filter fields
 * @param hasLogicalOperator Display/hide logical operator
 * @param disableBackground show filer background
 * @param isOrphan Is single filter
 * @param onInsertFilter New filter handler
 * @param onRemoveFilter Remove filter handle
 * @constructor
 */
export const Filter: React.FC<FieldFilterProps> = ({
    index,
    item,
    source,
    hasLogicalOperator,
    disableBackground,
    isOrphan,
    onInsertFilter,
    onRemoveFilter,
}) => {
    const filterNameKey = `filters[${index}].name`;
    const filterOperatorKey = `filters[${index}].operator`;
    const filterTypeKey = `filters[${index}].type`;
    const { control, errors } = useFormContext<{ filters: FilterViewModel[] }>();
    const [disableOperations, setDisableOperations] = useState(true);
    const [fieldName, setFieldName] = useState("");
    const [operatorName, setOperatorName] = useState("");
    const fieldsDefinition = useSelector(getFiltersFields);
    const filterFieldOptions = useSelectOptions(fieldsDefinition);
    const filterOperationOptions = useFilterOperationOptions(fieldName, fieldsDefinition);
    const queryFn = useFilterAutocomplete(source, fieldName, operatorName);
    const filters = useWatch({
        control,
        name: [filterNameKey, filterOperatorKey],
    });

    useEffect(() => {
        if (filters[filterNameKey]) {
            const definition = fieldsDefinition.find(({ field }) => field === filters[filterNameKey]);
            setFieldName(filters[filterNameKey]);
            control.setValue(filterTypeKey, definition?.type);
        }
    }, [filters, control, fieldsDefinition, filterTypeKey, filterNameKey, index]);

    useEffect(() => {
        if (filters[filterOperatorKey]) {
            setOperatorName(filters[filterOperatorKey]);
        }
    }, [filters, filterOperatorKey]);

    useEffect(() => {
        if (fieldName) {
            setDisableOperations(false);
        }
    }, [fieldName]);

    return (
        <Box display="flex" mt={1} mb={1}>
            <Box
                alignItems="flex-start"
                display="flex"
                flex={0.9}
                p={disableBackground ? 0 : 1}
                mr={2}
                border={disableBackground ? 0 : 1}
                borderColor={disableBackground ? "" : "grey.400"}>
                {/*Added to determinate filter type inside form, read only!*/}
                <Controller name={`filters[${index}].type`} control={control} as={<HiddenTextField />} />

                <Box display="flex" flex={0.85}>
                    <Select
                        defaultValue={item.name}
                        name={`filters[${index}].name`}
                        title="Field"
                        options={filterFieldOptions}
                        error={errors.filters?.[index]?.name}
                        onChange={(onChange, value) => {
                            setFieldName(value);
                            onChange(value);
                        }}
                    />

                    <Select
                        disabled={disableOperations}
                        defaultValue={item.operator}
                        name={`filters[${index}].operator`}
                        error={errors.filters?.[index]?.operator}
                        onChange={(onChange, value) => {
                            setOperatorName(value);
                            onChange(value);
                        }}
                        title="Operator"
                        options={filterOperationOptions}
                    />

                    <AsyncAutocomplete
                        flex={2}
                        defaultValue={item.options}
                        freeSolo={true}
                        minLength={1}
                        name={`filters[${index}].options`}
                        clearOnChangeOf={fieldName}
                        isDisabled={disableOperations}
                        // TODO: temporary walk-around, for some reasons resolver is typing property as object
                        // eslint-disable-next-line
                        error={errors.filters?.[index]?.options as any}
                        searchFn={queryFn}
                    />
                </Box>

                <Box display="flex" flex={0.15} alignItems="center">
                    {hasLogicalOperator && (
                        <Select
                            defaultValue={logicalOperators[1].value}
                            name={`filters[${index}].logicalOperator`}
                            title="Logical"
                            error={errors.filters?.[index]?.logicalOperator}
                            options={logicalOperators}
                        />
                    )}
                </Box>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="flex-end" flex={0.1}>
                <IconButton onClick={() => onInsertFilter(index + 1)}>
                    <AddIcon color="primary" />
                </IconButton>
                <IconButton onClick={() => onRemoveFilter(index)} disabled={isOrphan}>
                    <ClearIcon color={isOrphan ? "disabled" : "error"} />
                </IconButton>
            </Box>
        </Box>
    );
};
